/**
 * Handles API errors and formats them for display
 * @param {Error} error - The error object from the API call
 * @returns {string} Formatted error message
 */
export const handleApiError = (error) => {
  console.error("API Error:", error)

  // Check if it's an axios error with a response
  if (error.response && error.response.data) {
    const { data } = error.response

    // If we have detailed validation errors, format them
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      const fieldErrors = data.errors.map((err) => `${err.field}: ${err.message}`)
      return `${data.error || "Validation Error"}\n${fieldErrors.join("\n")}`
    }

    // Otherwise return the main error message
    return data.error || data.message || "An unexpected error occurred"
  }

  // For network errors
  if (error.message && error.message.includes("Network Error")) {
    return "Network error. Please check your internet connection and try again."
  }

  // For timeout errors
  if (error.code === "ECONNABORTED") {
    return "Request timed out. Please try again later."
  }

  // For other errors
  return error.message || "An unexpected error occurred"
}

/**
 * Creates a standardized error object for use throughout the application
 * @param {Error|string} error - The error object or message
 * @param {string} title - Optional title for the error
 * @returns {Object} Standardized error object
 */
export const createErrorObject = (error, title = "Error") => {
  // If error is already in our format, return it
  if (error && typeof error === "object" && error.title && error.message) {
    return error
  }

  // If error is a string, use it as the message
  if (typeof error === "string") {
    return {
      title,
      message: error,
      details: null,
    }
  }

  // If error is an Error object
  if (error instanceof Error) {
    return {
      title,
      message: error.message || "An unexpected error occurred",
      details: error.stack,
    }
  }

  // If error is from API
  if (error && error.response) {
    const message = handleApiError(error)
    return {
      title: `${title} (${error.response.status})`,
      message,
      details: error.response.data,
    }
  }

  // Default case
  return {
    title,
    message: "An unexpected error occurred",
    details: error,
  }
}

